// src/lib/claimSession.js
//
// Shared call to the claim-session edge function. ResultCTA.js needs this
// in two places now — the existing primary "build the other free product"
// OTP flow, and the advisor-connect OTP flow — so the fetch/header shape
// lives here once instead of as two inline copies that could drift apart.
// Non-fatal by design (matches the existing pattern in AuthCallback.js /
// ResultCTA.js): a failed claim shouldn't strand the user, since the
// session and lead data still exist independently. Callers are
// responsible for checking the returned boolean and surfacing a failure
// to the person rather than assuming success — see the fix to
// ResultCTA.js's handleOtpSuccess for why that matters.
//
// sessionId (new): a draft calculator session has no public_token yet
// (see the draft/publish lifecycle in calculate-irpf/index.ts) — passing
// sessionId lets claim-session look the row up by its internal id
// instead. Triage callers, and any already-published calculator session,
// can still pass publicToken alone. At least one of the two must be
// provided — claim-session itself enforces this.
//
// consentPartnerShare (new): optional boolean, only ever passed by
// ResultCTA.js's handleAdvisorOtpSuccess — the one call site where the
// person has just been shown AdvisorConnectForm's partner-data-sharing
// disclosure before this claim runs. The plain "save and continue" flow
// (handleOtpSuccess) never passes this, and correctly omits it from the
// request — claim-session only records a consent fact when this key is
// explicitly present (see claim-session/index.ts step 7b). Don't pass
// `true` here speculatively; it should only ever reflect a disclosure the
// person actually just saw.

// marketingConsent (new): optional boolean, sent by ResultCTA.js's primary
// "save and continue" OTP flow — the one both calculator and triage
// results funnel through when not partner-linked. Unlike
// consentPartnerShare, this isn't scoped to a specific advisor-form
// disclosure — it's a plain subject-level fact (see consentEvents.ts's
// MARKETING_CONSENT_TEXT/hasMarketingConsent()). claim-session records
// it whenever this key is explicitly present (step 7c), same "only ever
// reflects a checkbox the person actually just saw" rule as
// consentPartnerShare above.

export async function claimSession({ product, publicToken, sessionId, accessToken, consentPartnerShare, marketingConsent }) {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claim-session`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          product,
          public_token: publicToken,
          session_id:   sessionId,
          ...(typeof consentPartnerShare === 'boolean' ? { consent_partner_share: consentPartnerShare } : {}),
          ...(typeof marketingConsent === 'boolean' ? { marketing_consent: marketingConsent } : {}),
        }),
      }
    )
    if (!res.ok) {
      console.error('claim-session responded with', res.status)
      return false
    }
    return true
  } catch (err) {
    console.error('claim-session error:', err)
    return false
  }
}